// Per-page <title> + <meta description> + canonical + hreflang manager.
//
// We don't ship react-helmet — this hook is enough for our V1 SEO needs.
// On every route render it (1) updates document.title and the meta
// description, (2) replaces our managed <link rel="canonical"> tag, and
// (3) writes per-language <link rel="alternate" hreflang> pairs so Google
// understands /foo and /zh/foo are the same page in different languages.
//
// All "managed" tags carry a data-i18n="managed" attribute so we can find
// and clear them on subsequent renders without disturbing any pre-existing
// <meta> in index.html.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  type SupportedLanguage,
  htmlLangFor,
  languageFromPath,
  pathInLanguage,
} from './i18n'

export const SITE_ORIGIN = 'https://www.nimvoy.com'

interface DocumentMetaOptions {
  title: string
  description?: string
  // When true, emit <meta name="robots" content="noindex"> — for private
  // pages (account, checkout) where we don't want Google indexing.
  noindex?: boolean
}

export function useDocumentMeta({ title, description, noindex }: DocumentMetaOptions) {
  const location = useLocation()
  const { i18n } = useTranslation()
  // Canonical and hreflang are URL facts, so they stay path-derived.
  const lang = languageFromPath(location.pathname)
  // <html lang> is a fact about the text on screen, which is not always the
  // same thing: the partner portal lives outside the locale-prefixed routes
  // and switches language in place, so its path always reads as 'en'. Keying
  // the attribute off the path there left a fully Chinese page announcing
  // itself as English — wrong screen-reader voice, and Chrome offering to
  // translate it. The active i18n language is the honest answer everywhere;
  // on customer routes LanguageSync sets it FROM the path, so they agree.
  const activeLang = (i18n.language || lang) as SupportedLanguage

  useEffect(() => {
    document.title = title

    setManagedMeta('description', description ?? null)
    setManagedMeta('robots', noindex ? 'noindex, nofollow' : null)

    setManagedLink('canonical', SITE_ORIGIN + location.pathname, undefined)

    // hreflang set — every page maps to all 8 locales (en / zh-CN /
    // es-419 / fr / ja / ko / de / it), plus x-default pointing at the
    // English version (Google's recommended signal for fallback when
    // the user's language isn't matched).
    const enPath = pathInLanguage(location.pathname, 'en')
    const zhPath = pathInLanguage(location.pathname, 'zh')
    const esPath = pathInLanguage(location.pathname, 'es')
    const frPath = pathInLanguage(location.pathname, 'fr')
    const jaPath = pathInLanguage(location.pathname, 'ja')
    const koPath = pathInLanguage(location.pathname, 'ko')
    const dePath = pathInLanguage(location.pathname, 'de')
    const itPath = pathInLanguage(location.pathname, 'it')
    clearAlternates()
    if (!noindex) {
      addAlternate('en', SITE_ORIGIN + enPath)
      addAlternate('zh-CN', SITE_ORIGIN + zhPath)
      addAlternate('es-419', SITE_ORIGIN + esPath)
      addAlternate('fr', SITE_ORIGIN + frPath)
      addAlternate('ja', SITE_ORIGIN + jaPath)
      addAlternate('ko', SITE_ORIGIN + koPath)
      addAlternate('de', SITE_ORIGIN + dePath)
      addAlternate('it', SITE_ORIGIN + itPath)
      addAlternate('x-default', SITE_ORIGIN + enPath)
    }

    // Belt-and-suspenders alongside LanguageSync in App.tsx, which only runs
    // after mount; for direct landings this avoids a first-render gap.
    document.documentElement.lang = htmlLangFor(activeLang)
  }, [title, description, noindex, location.pathname, lang, activeLang])
}

function setManagedMeta(name: string, content: string | null) {
  const selector = `meta[name="${name}"][data-i18n="managed"]`
  const existing = document.head.querySelector<HTMLMetaElement>(selector)
  if (content === null) {
    if (existing) existing.remove()
    return
  }
  if (existing) {
    existing.content = content
    return
  }
  // If there's an unmanaged tag of the same name in index.html (e.g. the
  // baseline description), update it in place rather than appending a
  // second one — duplicate meta tags confuse some crawlers.
  const baseline = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (baseline) {
    baseline.content = content
    baseline.setAttribute('data-i18n', 'managed')
    return
  }
  const el = document.createElement('meta')
  el.name = name
  el.content = content
  el.setAttribute('data-i18n', 'managed')
  document.head.appendChild(el)
}

function setManagedLink(rel: string, href: string, hreflang: string | undefined) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"][data-i18n="managed"]`
    : `link[rel="${rel}"][data-i18n="managed"]`
  const existing = document.head.querySelector<HTMLLinkElement>(selector)
  if (existing) {
    existing.href = href
    return
  }
  const el = document.createElement('link')
  el.rel = rel
  el.href = href
  if (hreflang) el.setAttribute('hreflang', hreflang)
  el.setAttribute('data-i18n', 'managed')
  document.head.appendChild(el)
}

function clearAlternates() {
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][data-i18n="managed"]')
    .forEach((el) => el.remove())
}

function addAlternate(hreflang: string, href: string) {
  setManagedLink('alternate', href, hreflang)
}

// Convenience: same as the hook but as a one-off effect for routes that
// don't care about reactivity. Re-exported for tests / sandbox callers.
export type { SupportedLanguage }
